import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  message,
  Select,
  Table,
  Tag,
  Upload,
  Typography,
} from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { fetchAllCustomers } from 'src/features/CustomerSlice'
import { API } from 'src/utils/interceptor'

const { Paragraph, Title, Text } = Typography
const { TextArea } = Input
const { Dragger } = Upload

const CustomerInvoices = () => {
  const dispatch = useDispatch()
  const account = useSelector(state => state.auth.account)
  const supplierIds = useMemo(
    () => account?.supplier?.map(s => s._id).filter(Boolean) || [],
    [account],
  )
  const supplierId = supplierIds[0]
  const customers = useSelector(state => state.customer?.customers || [])
  const customersLoading = useSelector(state => state.customer.isLoading)

  useEffect(() => {
    if (supplierIds.length > 0) {
      dispatch(fetchAllCustomers(supplierIds))
    }
  }, [dispatch, supplierIds])

  const [selectedVenueId, setSelectedVenueId] = useState(null)
  const [fileList, setFileList] = useState([])
  const [meta, setMeta] = useState({
    invoiceNumber: '',
    invoiceDate: null,
    notes: '',
  })
  const [uploads, setUploads] = useState([])
  const [summary, setSummary] = useState({
    totalUploads: 0,
    selectedVenueUploads: null,
    lastUpload: null,
  })
  const [uploadsLoading, setUploadsLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const venueOptions = useMemo(() => {
    const base =
      customers?.map(customer => ({
        value: customer._id || customer.venueId,
        label: customer.venueTitle || 'Untitled venue',
        accountNumber: customer.accountNumber || 'â€”',
      })) || []

    // Ensure mock uploads have matches even before API data loads
    if (base.length === 0) {
      return [
        { value: 'mock-1', label: 'Bonnie Burrito', accountNumber: 'BB001' },
        { value: 'mock-2', label: 'Golf Tavern', accountNumber: 'GT777' },
      ]
    }
    return base
  }, [customers])

  const selectedVenue = useMemo(
    () => venueOptions.find(option => option.value === selectedVenueId),
    [venueOptions, selectedVenueId],
  )

  const totalUploads = summary?.totalUploads ?? 0
  const uploadsForVenue = selectedVenueId
    ? summary?.selectedVenueUploads ?? 0
    : totalUploads
  const lastUpload = summary?.lastUpload || null

  const fetchUploads = useCallback(
    async params => {
      if (!supplierId) return
      setUploadsLoading(true)
      try {
        const response = await API.get(
          'api2',
          `suppliers/${supplierId}/customer-invoices`,
          {
            queryStringParameters: params,
          },
        )
        setUploads(response?.data?.uploads || [])
        setSummary(response?.data?.summary || {})
      } catch (error) {
        console.error('Failed to load supplier invoices', error)
        message.error('Unable to load customer invoices right now.')
      } finally {
        setUploadsLoading(false)
      }
    },
    [supplierId],
  )

  useEffect(() => {
    if (supplierId) {
      const query = selectedVenueId ? { venueId: selectedVenueId } : undefined
      fetchUploads(query)
    }
  }, [supplierId, selectedVenueId, fetchUploads])

  const handleUploadChange = ({ fileList: incomingList }) => {
    setFileList(incomingList)
  }

  const handleUpload = async () => {
    if (!selectedVenueId) {
      message.warning('Choose a customer before uploading.')
      return
    }
    if (fileList.length === 0) {
      message.warning('Add at least one invoice file.')
      return
    }

    const formData = new FormData()
    formData.append('venueId', selectedVenueId)
    if (meta.invoiceNumber) {
      formData.append('invoiceNumber', meta.invoiceNumber.trim())
    }
    if (meta.invoiceDate) {
      formData.append('invoiceDate', meta.invoiceDate.toISOString())
    }
    if (meta.notes) {
      formData.append('notes', meta.notes.trim())
    }

    fileList.forEach(file => {
      const actualFile = file.originFileObj || file
      formData.append('files', actualFile)
    })

    setUploading(true)
    try {
      const response = await API.post(
        'api2',
        `suppliers/${supplierId}/customer-invoices`,
        {
          body: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      const count = response?.data?.length || fileList.length
      message.success(
        `Successfully uploaded ${count} invoice${count !== 1 ? 's' : ''}.`,
      )
      setFileList([])
      setMeta({ invoiceNumber: '', invoiceDate: null, notes: '' })
      fetchUploads(selectedVenueId ? { venueId: selectedVenueId } : undefined)
    } catch (error) {
      console.error('Failed to upload invoices', error)
      const errorMsg =
        error?.response?.data?.message || 'Unable to upload invoices right now.'
      message.error(errorMsg)
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async record => {
    const firstFile = record.files?.[0]
    if (!firstFile?.url) {
      message.info('File is not available yet.')
      return
    }

    try {
      const response = await fetch(firstFile.url)

      if (!response.ok) {
        throw new Error(
          `Failed to download invoice. Status: ${response.status}`,
        )
      }

      const contentType = response.headers.get('Content-Type')
      const isPdf = contentType && contentType.includes('application/pdf')
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch =
        contentDisposition && contentDisposition.match(/filename="(.+?)"/)
      const filename = filenameMatch
        ? filenameMatch[1]
        : firstFile.fileName || (isPdf ? 'invoice.pdf' : 'invoice.jpg')

      const blob = await response.blob()

      const downloadLink = document.createElement('a')
      downloadLink.href = URL.createObjectURL(blob)
      downloadLink.download = filename

      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    } catch (error) {
      console.error('Error downloading invoice:', error.message)
      message.error('Failed to download invoice. Please try again.')
    }
  }

  const columns = [
    {
      title: 'Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => moment(value).format('DD MMM YYYY, HH:mm'),
      sorter: (a, b) =>
        moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Venue',
      dataIndex: 'venueTitle',
      key: 'venueTitle',
      render: (_, record) => record.venueTitle || record.venueName || 'â€”',
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
    },
    {
      title: 'File',
      key: 'fileName',
      render: record => {
        if (!record.files || record.files.length === 0) {
          return 'Not available'
        }
        const firstFile = record.files[0]
        const extraCount = record.files.length - 1
        return (
          <span>
            {firstFile.name}
            {extraCount > 0 && (
              <Tag style={{ marginLeft: 8 }}>+{extraCount}</Tag>
            )}
          </span>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Tag color={status === 'Reconciled' ? 'green' : 'gold'}>{status}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: record => (
        <Button type='link' size='small' onClick={() => handleDownload(record)}>
          Download
        </Button>
      ),
    },
  ]

  return (
    <PageWrapper>
      <HeroCard>
        <Title level={2}>Customer Invoices</Title>
        <Paragraph>
          Upload invoices on behalf of your venues so the OMS team can reconcile
          them against their orders. It isnâ€™t mandatory, but some venues will
          ask you to handle this for them so their statements stay tidy.
        </Paragraph>
        <Bullets>
          <li>Choose the venue, drag in the invoice file, and hit upload.</li>
          <li>
            All fields are optional. Provide any invoice details you have.
          </li>
          <li>
            Each upload notifies the OMS invoicing team to reconcile the orders.
          </li>
        </Bullets>
      </HeroCard>

      <StatsRow>
        <Card>
          <StatLabel>Invoices uploaded</StatLabel>
          <StatValue>{totalUploads}</StatValue>
          <StatHelper>Across all of your venues</StatHelper>
        </Card>
        <Card>
          <StatLabel>Selected venue</StatLabel>
          <StatValue>{selectedVenue ? uploadsForVenue : 'â€”'}</StatValue>
          <StatHelper>
            {selectedVenue
              ? `${selectedVenue.label}`
              : 'Pick a venue to view stats'}
          </StatHelper>
        </Card>
        <Card>
          <StatLabel>Last upload</StatLabel>
          <StatValue>
            {lastUpload ? moment(lastUpload).fromNow() : 'No uploads yet'}
          </StatValue>
          <StatHelper>
            {lastUpload && moment(lastUpload).format('DD MMM YYYY')}
          </StatHelper>
        </Card>
      </StatsRow>

      <Grid>
        <LeftCol>
          <Card title='Upload invoices'>
            <Field>
              <FieldLabel>Choose a customer</FieldLabel>
              <Select
                showSearch
                placeholder='Search by venue or account number'
                value={selectedVenueId}
                onChange={value => setSelectedVenueId(value)}
                optionFilterProp='label'
                loading={customersLoading}
              >
                {venueOptions.map(option => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={`${option.label} (Account ${option.accountNumber})`}
                  >
                    <div className='optionRow'>
                      <span>{option.label}</span>
                      <small>(Account {option.accountNumber})</small>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Field>

            <FieldGroup>
              <Field>
                <FieldLabel>Invoice number</FieldLabel>
                <Input
                  placeholder='Optional'
                  value={meta.invoiceNumber}
                  onChange={e =>
                    setMeta(current => ({
                      ...current,
                      invoiceNumber: e.target.value,
                    }))
                  }
                />
              </Field>
              <Field>
                <FieldLabel>Invoice date</FieldLabel>
                <DatePicker
                  placeholder='Optional'
                  style={{ width: '100%' }}
                  value={meta.invoiceDate}
                  onChange={value =>
                    setMeta(current => ({ ...current, invoiceDate: value }))
                  }
                />
              </Field>
            </FieldGroup>

            <Field>
              <FieldLabel>Notes</FieldLabel>
              <TextArea
                placeholder='Anything you want to flag for this upload'
                rows={3}
                value={meta.notes}
                onChange={e =>
                  setMeta(current => ({ ...current, notes: e.target.value }))
                }
              />
            </Field>

            <Field>
              <FieldLabel>Invoice files</FieldLabel>
              <Dragger
                multiple
                beforeUpload={() => false}
                fileList={fileList}
                onChange={handleUploadChange}
                accept='.pdf,.png,.jpg,.jpeg'
              >
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>
                  Click or drag files to this area to upload
                </p>
                <p className='ant-upload-hint'>
                  PDF, JPG or PNG. You can add multiple invoices at once.
                </p>
              </Dragger>
            </Field>

            <Button
              type='primary'
              size='large'
              block
              onClick={handleUpload}
              loading={uploading}
            >
              Upload invoices
            </Button>
          </Card>
        </LeftCol>

        <RightCol>
          <Card
            title={
              selectedVenue
                ? `Recent uploads for ${selectedVenue.label}`
                : 'Recent uploads'
            }
          >
            <Table
              rowKey={record => record._id}
              dataSource={uploads}
              columns={columns}
              loading={uploadsLoading}
              pagination={{ pageSize: 6, size: 'small' }}
              locale={{
                emptyText: (
                  <Empty
                    description={
                      selectedVenue
                        ? 'No invoices uploaded for this customer yet'
                        : 'No invoices uploaded yet'
                    }
                  />
                ),
              }}
            />
          </Card>
        </RightCol>
      </Grid>
    </PageWrapper>
  )
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const HeroCard = styled(Card)`
  border-radius: 16px;
  box-shadow: var(--sp-shadow-card);
  background: #fff;
  .ant-card-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`

const Bullets = styled.ul`
  padding-left: 20px;
  margin: 0;
  color: #475467;
  li {
    margin-bottom: 4px;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`

const FieldLabel = styled(Text)`
  font-weight: 600;
`

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;

  .ant-card {
    border-radius: 14px;
  }
`

const StatLabel = styled(Text)`
  font-size: 13px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

const StatValue = styled(Text)`
  display: block;
  font-size: 26px;
  font-weight: 700;
  margin-top: 8px;
`

const StatHelper = styled.span`
  color: #475467;
  font-size: 13px;
`

export default CustomerInvoices
